import DataService from "../service/DataService";
import { getRemoteEnv } from '../config/remoteEnv';
import { where } from "firebase/firestore";

interface Config {
  properties: any;
  restaurants: any;
}

interface StoreDocument {
  id: string;
  configs?: Record<string, any>;
  [key: string]: any;
}

let runtimeConfig: Config = { properties: {}, restaurants: {} };

// Cache keys are scoped PER APP: every /r/{app} brand shares this origin's
// localStorage, and the old unscoped "configCache" served one brand's whole
// config (name, logo, restaurants) to another for up to a minute after
// cross-brand navigation.
const cacheScope = (): string => {
  try { return getRemoteEnv().appId || "noapp"; } catch { return "noapp"; }
};
const CACHE_KEY_        = "configCache";
const CACHE_TIME_KEY_   = "configCacheTime";
const CACHE_VERSION_KEY_ = "configCacheVersion";
const k = (base: string) => `${base}:${cacheScope()}`;
const CACHE_LIFETIME    = 1 * 60 * 1000; // 1 minute

// Bump this number whenever the cache structure changes in a way that makes
// old cached data invalid (e.g. DB migrations, doc ID renames).
// Any cache written with a different version is automatically discarded
// on the user's next visit — no manual localStorage clearing needed.
const CACHE_VERSION = 2; // bumped for storeConfig → default DB migration

/**
 * Returns true if a given doc/key ID is the properties document
 * for the current app. Handles both the legacy "properties" ID
 * (pre-migration data) and the new "{appId}_properties" form.
 */
function isPropertiesDoc(id: string): boolean {
  const appId = getRemoteEnv().appId;
  return id === "properties" || (appId ? id === `${appId}_properties` : false);
}

// Function to load from storeConfig — original fallback method
const loadFromStoreConfig = async () => {
  console.log("Loading from storeConfig directly");
  // const storeConfig = await DataService.GetData("storeConfig");
  const appId = getRemoteEnv().appId;
  const storeConfig = await DataService.GetData(
            "storeConfig",
            undefined,
            where("appId", "==", appId),
            undefined,
            true
        );
  console.log("storeConfig data: ", storeConfig, JSON.stringify(storeConfig));

  let restaurantConfig: any = {};
  let properties: any = {};

  for (let item of storeConfig) {
    if (!isPropertiesDoc(item.id)) {
      restaurantConfig[item.id] = item;
    } else {
      properties = item;
    }
  }

  runtimeConfig.restaurants = restaurantConfig;
  runtimeConfig.properties  = properties;

  saveToCache();
  console.log("Configuration loaded from storeConfig and cached");
};

const validateRestaurantStructure = (restaurant: any, id: string): boolean => {
  const requiredProps = [
    'id', 'name', 'delivery', 'pickup', 'dinein',
    'general', 'defaultTimings', 'additionalCharges'
  ];

  for (const prop of requiredProps) {
    if (restaurant[prop] === undefined) {
      console.warn(`Restaurant ${id} is missing required property: ${prop}`);
      return false;
    }
  }

  return true;
};

// Function to load from storeConfigCache — optimised primary method
const loadFromStoreConfigCache = async () => {
  console.log("Loading from storeConfigCache");
  const storeConfigCache: StoreDocument[] = await DataService.GetData("storeConfigCache");

  if (!storeConfigCache || storeConfigCache.length === 0) {
    console.warn("No storeConfigCache data found");
    throw new Error("No storeConfigCache data found");
  }

  console.log("Cache structure:", storeConfigCache.map((doc: StoreDocument) => doc.id));

  let tempRestaurants: Record<string, any> = {};
  let tempProperties: any = null;

  for (const doc of storeConfigCache) {
    // Skip metadata sentinel docs (e.g. {appId}_metadata)
    if (doc.id.endsWith("_metadata") || doc.id === "metadata") continue;

    if (!doc.configs || typeof doc.configs !== 'object') {
      console.warn(`Document ${doc.id} has invalid configs structure`);
      continue;
    }

    Object.entries(doc.configs).forEach(([key, value]: [string, any]) => {
      if (isPropertiesDoc(key)) {
        // Normalise to "properties" so the rest of the app doesn't need
        // to know about the {appId}_properties naming convention
        tempProperties = { ...value, id: "properties" };
      } else {
        const restaurant = { ...value, id: key };
        if (validateRestaurantStructure(restaurant, key)) {
          tempRestaurants[key] = restaurant;
        } else {
          console.warn(`Skipping restaurant ${key} due to missing required properties`);
        }
      }
    });
  }

  if (!tempProperties) {
    console.warn("Properties not found in storeConfigCache");
    throw new Error("Properties not found in storeConfigCache");
  }

  if (Object.keys(tempRestaurants).length === 0) {
    console.warn("No valid restaurants found in storeConfigCache");
    throw new Error("No valid restaurants found in storeConfigCache");
  }

  runtimeConfig.restaurants = tempRestaurants;
  runtimeConfig.properties  = tempProperties;

  saveToCache();
  console.log(`Configuration loaded from storeConfigCache with ${Object.keys(tempRestaurants).length} restaurants`);
};

/**
 * Writes the current runtimeConfig to localStorage along with the
 * current timestamp and cache version.
 */
function saveToCache(): void {
  localStorage.setItem(k(CACHE_KEY_),         JSON.stringify(runtimeConfig));
  localStorage.setItem(k(CACHE_TIME_KEY_),    Date.now().toString());
  localStorage.setItem(k(CACHE_VERSION_KEY_), CACHE_VERSION.toString());
}

/**
 * Returns true if a valid, unexpired, version-matching cache exists.
 * If the version doesn't match (e.g. after a migration), the stale
 * cache entries are deleted so a fresh fetch will be performed.
 */
function isCacheValid(): boolean {
  const cachedConfig  = localStorage.getItem(k(CACHE_KEY_));
  const cachedTime    = localStorage.getItem(k(CACHE_TIME_KEY_));
  const cachedVersion = localStorage.getItem(k(CACHE_VERSION_KEY_));

  if (!cachedConfig || !cachedTime) return false;

  // Version mismatch — wipe the stale cache so it doesn't get used
  if (cachedVersion !== CACHE_VERSION.toString()) {
    console.log(`Cache version mismatch (stored: ${cachedVersion}, current: ${CACHE_VERSION}). Clearing stale cache.`);
    localStorage.removeItem(k(CACHE_KEY_));
    localStorage.removeItem(k(CACHE_TIME_KEY_));
    localStorage.removeItem(k(CACHE_VERSION_KEY_));
    return false;
  }

  const age = Date.now() - parseInt(cachedTime, 10);
  return age < CACHE_LIFETIME;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** One attempt: primary (storeConfigCache) then fallback (storeConfig). */
const loadOnce = async (): Promise<void> => {
  try {
    await loadFromStoreConfigCache();
  } catch (error) {
    console.warn("Error loading from storeConfigCache, falling back to storeConfig", error);
    await loadFromStoreConfig();
  }
};

export const loadConfig = async () => {
  console.log("load config call");

  if (isCacheValid()) {
    console.log("Loaded configuration from browser cache");
    runtimeConfig = JSON.parse(localStorage.getItem(k(CACHE_KEY_))!);
    return;
  }

  // A transient network/Firestore hiccup on a fresh device (no cache to
  // fall back on) used to surface "Error Loading Configuration" straight
  // away — the intermittent demo-day failure. Retry with backoff first.
  const delays = [0, 800, 2000];
  let lastError: unknown = null;
  for (const delay of delays) {
    if (delay) await sleep(delay);
    try {
      await loadOnce();
      return;
    } catch (err) {
      lastError = err;
      console.warn("Config load attempt failed, will retry:", err);
    }
  }

  console.error("Error loading configuration after retries:", lastError);
  const lastCachedConfig = localStorage.getItem(k(CACHE_KEY_));
  if (lastCachedConfig) {
    // Expired cache beats an error screen.
    console.log("Using last cached configuration due to errors");
    runtimeConfig = JSON.parse(lastCachedConfig);
  } else {
    console.error("No cached configuration available as fallback");
    throw lastError;
  }
};

export const getConfig = (): Config => {
  console.log("get config call");
  if (!runtimeConfig.properties || Object.keys(runtimeConfig.properties).length === 0) {
    throw new Error("Configuration not loaded. Call loadConfig() first.");
  }
  return runtimeConfig;
};